'use client'

import React from 'react'
import '../styles/partnership.css'
import Master from '@components/Layout/Master'
import { useLanguage } from '@contexts/LanguageContext'

const BENEFITS = [
  { icon: '🤝', title: 'meaningfulImpact', desc: 'meaningfulImpactDesc' },
  { icon: '📊', title: 'transparentReporting', desc: 'transparentReportingDesc' },
  { icon: '🏫', title: 'accessToSchools', desc: 'accessToSchoolsDesc' },
  { icon: '💼', title: 'brandVisibility', desc: 'brandVisibilityDesc' },
] as const

const PARTNERSHIP_TYPES = [
  {
    title: 'corporatePartnership',
    desc: 'corporatePartnershipFull',
    features: ['corporateFeature1', 'corporateFeature2', 'corporateFeature3', 'corporateFeature4'],
  },
  {
    title: 'universityPartnership',
    desc: 'universityPartnershipDesc',
    features: [
      'universityFeature1',
      'universityFeature2',
      'universityFeature3',
      'universityFeature4',
    ],
  },
  {
    title: 'foundationPartnership',
    desc: 'foundationPartnershipFull',
    features: [
      'foundationFeature1',
      'foundationFeature2',
      'foundationFeature3',
      'foundationFeature4',
    ],
  },
  {
    title: 'ngoPartnership',
    desc: 'ngoPartnershipFull',
    features: ['ngoFeature1', 'ngoFeature2', 'ngoFeature3', 'ngoFeature4'],
  },
] as const

const PROCESS_STEPS = [
  { title: 'initialConsultation', desc: 'initialConsultationDesc' },
  { title: 'customizedProposal', desc: 'customizedProposalDesc' },
  { title: 'implementation', desc: 'implementationDesc' },
  { title: 'impactReporting', desc: 'impactReportingDesc' },
] as const

const PartnershipPage: React.FC = () => {
  const { t } = useLanguage()

  return (
    <Master>
      {/* Hero Section */}
      <section className="partnership-hero">
        <div className="partnership-hero-content">
          <h1 className="partnership-hero-title">{t('partnershipHeroTitle')}</h1>
          <p className="partnership-hero-subtitle">{t('partnershipHeroSubtitle')}</p>
          <a href="/contact" className="btn-hero">
            {t('partnerHeroCta')}
          </a>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="partnership-why">
        <div className="container">
          <h2 className="section-title">{t('whyPartner')}</h2>
          <div className="partnership-benefits-grid">
            {BENEFITS.map((benefit) => (
              <div key={benefit.title} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{t(benefit.title)}</h3>
                <p>{t(benefit.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Types */}
      <section className="partnership-types">
        <div className="container">
          <h2 className="section-title">{t('partnershipOpportunities')}</h2>
          <div className="partnership-types-grid">
            {PARTNERSHIP_TYPES.map((type) => (
              <div key={type.title} className="type-card">
                <h3>{t(type.title)}</h3>
                <p>{t(type.desc)}</p>
                <ul className="type-features">
                  {type.features.map((feature) => (
                    <li key={feature}>{t(feature)}</li>
                  ))}
                </ul>
                <a href="/contact" className="btn-outline">
                  {t('learnMore')}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="partnership-stories">
        <div className="container">
          <h2 className="section-title">{t('partnershipSuccessStories')}</h2>
          <div className="stories-grid stories-grid-single">
            <div className="story-card">
              <div className="story-image-placeholder">
                <div className="story-icon">🏆</div>
              </div>
              <div className="story-content">
                <h4>D-Prize</h4>
                <p className="story-description">{t('dPrizeStoryDesc')}</p>
                <blockquote className="story-quote-text">{t('dPrizeQuote')}</blockquote>
                <p className="story-attribution">{t('dPrizeAttribution')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="partnership-process">
        <div className="container">
          <h2 className="section-title">{t('howPartnershipWorks')}</h2>
          <div className="process-steps">
            {PROCESS_STEPS.map((step, index) => (
              <div key={step.title} className="process-step">
                <div className="step-number">{index + 1}</div>
                <h4>{t(step.title)}</h4>
                <p>{t(step.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="partnership-cta">
        <div className="container">
          <div className="cta-content">
            <h2>{t('readyToCreateImpact')}</h2>
            <p>{t('readyToCreateImpactDesc')}</p>
            <div className="cta-buttons">
              <a href="/contact" className="btn-primary-large">
                {t('startConversation')}
              </a>
              <a href="/about" className="btn-outline-light">
                {t('learnAboutUs')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </Master>
  )
}

export default PartnershipPage
